import React from 'react';
import { formatDistanceToNow } from 'date-fns';
import { fr } from 'date-fns/locale';
import { Reply, ThumbsUp, ThumbsDown, Flag, Trash2, AlertTriangle, MessageSquare, ArrowUp, MoveRight, X, User, Star, Mail, Smile, CornerDownRight } from 'lucide-react';
import { useAuth } from '../../lib/auth-context';
import { budgetCountries } from '../../data/budget-countries';
import { doc, updateDoc, arrayUnion, arrayRemove, deleteDoc, getDocs, collection, query, where, writeBatch } from 'firebase/firestore';
import { db } from '../../lib/firebase';
import { createNotification } from '../../lib/notification-service';

const QUICK_EMOJIS = ['👍', '❤️', '😂', '😮', '🎉', '🔥'];

const AVATAR_COLORS = [
  'from-sky-400 to-blue-500',
  'from-emerald-400 to-teal-500',
  'from-amber-400 to-orange-500',
  'from-rose-400 to-pink-500',
  'from-cyan-400 to-sky-500',
  'from-lime-400 to-emerald-500',
  'from-teal-400 to-cyan-500',
  'from-blue-400 to-sky-600',
];

const getAvatarColor = (userId: string) => {
  let hash = 0;
  for (let i = 0; i < userId.length; i++) {
    hash = userId.charCodeAt(i) + ((hash << 5) - hash);
  }
  return AVATAR_COLORS[Math.abs(hash) % AVATAR_COLORS.length];
};

const playReactionSound = (pitch: number) => {
  try {
    const audioContext = new (window.AudioContext || (window as any).webkitAudioContext)();
    const oscillator = audioContext.createOscillator();
    const gainNode = audioContext.createGain();
    oscillator.connect(gainNode);
    gainNode.connect(audioContext.destination);
    oscillator.frequency.value = pitch;
    oscillator.type = 'sine';
    gainNode.gain.setValueAtTime(0.2, audioContext.currentTime);
    gainNode.gain.exponentialRampToValueAtTime(0.01, audioContext.currentTime + 0.15);
    oscillator.start(audioContext.currentTime);
    oscillator.stop(audioContext.currentTime + 0.15);
  } catch {}
};

interface Message {
  id: string;
  content: string;
  user_id: string;
  user_email: string;
  username: string;
  created_at: { toDate: () => Date };
  likes: string[];
  dislikes: string[];
  thread_code?: string;
  thread_type?: 'country' | 'general' | 'bug';
  reply_to?: {
    id: string;
    user_email: string;
    username: string;
    user_id?: string;
  };
  is_admin?: boolean;
  emoji_reactions?: Record<string, string[]>;
}

interface MessageListProps {
  messages: Message[];
  onReply: (message: Message) => void;
  soundEnabled?: boolean;
}

const formatDate = (date: Date) => {
  return formatDistanceToNow(date, { addSuffix: true, locale: fr }).replace('environ ', '');
};

export function MessageList({ messages, onReply, soundEnabled = true }: MessageListProps) {
  const { user, userInfo, isAdmin } = useAuth();
  const [messageToDelete, setMessageToDelete] = React.useState<Message | null>(null);
  const [sortNewestFirst, setSortNewestFirst] = React.useState(() => {
    const savedSort = localStorage.getItem('messageSort');
    return savedSort === null ? false : savedSort === 'newest';
  });
  const messagesStartRef = React.useRef<HTMLDivElement>(null);
  const scrollContainerRef = React.useRef<HTMLDivElement>(null);
  const [prevMessagesLength, setPrevMessagesLength] = React.useState(0);
  const [displayLimit, setDisplayLimit] = React.useState(15);
  const [showMoveModal, setShowMoveModal] = React.useState(false);
  const [messageToMove, setMessageToMove] = React.useState<Message | null>(null);
  const [targetThread, setTargetThread] = React.useState<string>('');
  const [availableThreads] = React.useState<{code: string, name: string, type: 'country' | 'general' | 'bug'}[]>([
    { code: 'general_discussion', name: 'Discussion generale', type: 'general' },
    { code: 'bug_report', name: 'Signalements de bugs', type: 'bug' },
    ...budgetCountries.map(country => ({
      code: country.code,
      name: country.name,
      type: 'country' as const
    }))
  ]);
  const [isMoving, setIsMoving] = React.useState(false);
  const [replyToMessage, setReplyToMessage] = React.useState<Message | null>(null);
  const [reactionError, setReactionError] = React.useState<string | null>(null);

  React.useEffect(() => {
    localStorage.setItem('messageSort', sortNewestFirst ? 'newest' : 'oldest');
  }, [sortNewestFirst]);

  React.useEffect(() => {
    if (messages.length > prevMessagesLength && scrollContainerRef.current) {
      const previousScrollHeight = scrollContainerRef.current.scrollHeight;
      const previousScrollTop = scrollContainerRef.current.scrollTop;
      setTimeout(() => {
        if (scrollContainerRef.current) {
          const newScrollHeight = scrollContainerRef.current.scrollHeight;
          const heightDifference = newScrollHeight - previousScrollHeight;
          scrollContainerRef.current.scrollTop = previousScrollTop + heightDifference;
        }
      }, 0);
    }
    setPrevMessagesLength(messages.length);
  }, [messages, prevMessagesLength]);

  const buildMessageTree = React.useMemo(() => {
    const messageMap = new Map<string, Message[]>();
    messages.forEach(message => {
      if (message.reply_to) {
        const parentId = message.reply_to.id;
        if (!messageMap.has(parentId)) {
          messageMap.set(parentId, []);
        }
        messageMap.get(parentId)?.push(message);
      }
    });
    messageMap.forEach(replies => {
      replies.sort((a, b) => a.created_at.toDate().getTime() - b.created_at.toDate().getTime());
    });
    return messageMap;
  }, [messages]);

  const topLevelMessages = React.useMemo(() => {
    return messages
      .filter(message => !message.reply_to)
      .sort((a, b) => {
        const aTime = a.created_at.toDate().getTime();
        const bTime = b.created_at.toDate().getTime();
        return sortNewestFirst ? bTime - aTime : aTime - bTime;
      });
  }, [messages, sortNewestFirst]);

  const visibleMessages = React.useMemo(() => {
    return topLevelMessages.slice(-displayLimit);
  }, [topLevelMessages, displayLimit]);

  const hasMoreMessages = topLevelMessages.length > displayLimit;

  const handleReaction = async (messageId: string, isLike: boolean) => {
    if (!user) return;
    try {
      const messageRef = doc(db, 'messages', messageId);
      const message = messages.find(m => m.id === messageId);
      if (!message) return;
      if (message.user_id === user.uid) return;
      const hasLiked = (message.likes || []).includes(user.uid);
      const hasDisliked = (message.dislikes || []).includes(user.uid);
      if (isLike) {
        if (soundEnabled) playReactionSound(hasLiked ? 600 : 1000);
        if (hasDisliked) await updateDoc(messageRef, { dislikes: arrayRemove(user.uid) });
        await updateDoc(messageRef, { likes: hasLiked ? arrayRemove(user.uid) : arrayUnion(user.uid) });
        if (!hasLiked) {
          createNotification({
            recipientId: message.user_id,
            senderId: user.uid,
            senderUsername: userInfo?.user?.username || user.email || '',
            type: 'like',
            messageId,
            messagePreview: message.content,
            threadCode: message.thread_code || '',
            threadType: message.thread_type || 'general',
          });
        }
      } else {
        if (soundEnabled) playReactionSound(hasDisliked ? 500 : 400);
        if (hasLiked) await updateDoc(messageRef, { likes: arrayRemove(user.uid) });
        await updateDoc(messageRef, { dislikes: hasDisliked ? arrayRemove(user.uid) : arrayUnion(user.uid) });
        if (!hasDisliked) {
          createNotification({
            recipientId: message.user_id,
            senderId: user.uid,
            senderUsername: userInfo?.user?.username || user.email || '',
            type: 'dislike',
            messageId,
            messagePreview: message.content,
            threadCode: message.thread_code || '',
            threadType: message.thread_type || 'general',
          });
        }
      }
    } catch (error) {
      console.error('Error updating reaction:', error);
      setReactionError('Impossible d\'enregistrer la reaction. Reessayez.');
      setTimeout(() => setReactionError(null), 3000);
    }
  };

  const handleEmojiReaction = async (messageId: string, emoji: string) => {
    if (!user) return;
    try {
      const messageRef = doc(db, 'messages', messageId);
      const message = messages.find(m => m.id === messageId);
      if (!message) return;
      if (message.user_id === user.uid) return;
      const emojiReactions = message.emoji_reactions || {};
      const usersForEmoji: string[] = emojiReactions[emoji] || [];
      const hasReacted = usersForEmoji.includes(user.uid);
      const updatedUsers = hasReacted
        ? usersForEmoji.filter((uid: string) => uid !== user.uid)
        : [...usersForEmoji, user.uid];
      const updatedReactions = { ...emojiReactions, [emoji]: updatedUsers };
      if (updatedUsers.length === 0) delete updatedReactions[emoji];
      if (soundEnabled) playReactionSound(hasReacted ? 600 : 1200);
      await updateDoc(messageRef, { emoji_reactions: updatedReactions });
      if (!hasReacted) {
        createNotification({
          recipientId: message.user_id,
          senderId: user.uid,
          senderUsername: userInfo?.user?.username || user.email || '',
          type: 'emoji_reaction',
          messageId,
          messagePreview: message.content,
          threadCode: message.thread_code || '',
          threadType: message.thread_type || 'general',
          emoji,
        });
      }
    } catch (error) {
      console.error('Error updating emoji reaction:', error);
      setReactionError('Impossible d\'enregistrer la reaction. Reessayez.');
      setTimeout(() => setReactionError(null), 3000);
    }
  };

  const handleReport = async (messageId: string) => {
    if (!user) return;
    try {
      const messageRef = doc(db, 'messages', messageId);
      await updateDoc(messageRef, { reported: true, reported_by: user.uid, reported_at: new Date() });
      alert('Message signale aux moderateurs.');
    } catch (error) {
      console.error('Error reporting message:', error);
    }
  };

  const handleDelete = async () => {
    if (!messageToDelete || !isAdmin) return;
    try {
      await deleteDoc(doc(db, 'messages', messageToDelete.id));
      setMessageToDelete(null);
    } catch (error) {
      console.error('Error deleting message:', error);
      alert('Erreur lors de la suppression du message');
    }
  };

  const handleMoveMessage = (message: Message) => {
    setMessageToMove(message);
    setTargetThread('');
    setShowMoveModal(true);
  };

  const handleReply = (message: Message) => {
    setReplyToMessage(message);
    onReply({
      id: message.id,
      content: message.content,
      user_id: message.user_id,
      user_email: message.user_email,
      username: message.username,
      created_at: message.created_at,
      likes: message.likes,
      dislikes: message.dislikes
    });
  };

  const executeMove = async () => {
    if (!messageToMove || !targetThread || isMoving) return;
    setIsMoving(true);
    try {
      const batch = writeBatch(db);
      const messageRef = doc(db, 'messages', messageToMove.id);
      const targetThreadType = availableThreads.find(t => t.code === targetThread)?.type || 'country';
      batch.update(messageRef, { thread_code: targetThread, thread_type: targetThreadType });
      const directRepliesQuery = query(collection(db, 'messages'), where('reply_to.id', '==', messageToMove.id));
      const directRepliesSnapshot = await getDocs(directRepliesQuery);
      directRepliesSnapshot.forEach(replyDoc => {
        batch.update(doc(db, 'messages', replyDoc.id), { thread_code: targetThread, thread_type: targetThreadType });
      });
      await batch.commit();
      setShowMoveModal(false);
      setMessageToMove(null);
      setTargetThread('');
      alert(`Message deplace avec succes vers ${availableThreads.find(t => t.code === targetThread)?.name}`);
    } catch (error) {
      console.error('Erreur lors du deplacement du message:', error);
      alert('Erreur lors du deplacement du message');
    } finally {
      setIsMoving(false);
    }
  };

  const renderMessageWithReplies = (message: Message, level: number = 0, msgIndex: number = 0) => {
    const replies = buildMessageTree.get(message.id) || [];
    const visualLevel = Math.min(level, 1);
    return (
      <React.Fragment key={message.id}>
        <MessageComponent
          message={message}
          allMessages={messages}
          isReply={level > 0}
          replyToMessage={replyToMessage}
          isAdmin={isAdmin}
          userInfo={userInfo}
          user={user}
          index={msgIndex}
          onDelete={() => setMessageToDelete(message)}
          onReply={() => handleReply(message)}
          onReaction={handleReaction}
          onEmojiReaction={handleEmojiReaction}
          onReport={handleReport}
          onMove={() => handleMoveMessage(message)}
        />
        {replies.length > 0 && (
          <div className={visualLevel === 0 ? 'ml-5 sm:ml-10' : ''}>
            {replies.map((reply, rIdx) => renderMessageWithReplies(reply, level + 1, msgIndex + rIdx + 1))}
          </div>
        )}
      </React.Fragment>
    );
  };

  return (
    <div ref={scrollContainerRef} className="flex flex-col p-4 sm:p-6 space-y-1 custom-scrollbar">
      {reactionError && (
        <div className="sticky top-2 z-50 flex justify-center animate-fade-in">
          <div className="px-4 py-2 bg-red-50 dark:bg-red-900/30 text-red-600 dark:text-red-400 text-xs font-medium rounded-xl ring-1 ring-red-200 dark:ring-red-800 shadow-lg">
            {reactionError}
          </div>
        </div>
      )}
      <div ref={messagesStartRef} />

      {hasMoreMessages && (
        <div className="flex justify-center py-3">
          <button
            onClick={() => setDisplayLimit(prev => prev + 15)}
            className="flex items-center gap-2 px-4 py-2 text-xs font-semibold text-blue-600 dark:text-blue-400 bg-blue-50 dark:bg-blue-900/20 hover:bg-blue-100 dark:hover:bg-blue-900/30 rounded-full transition-colors"
          >
            <ArrowUp className="w-3 h-3" />
            <span>Charger plus de messages</span>
          </button>
        </div>
      )}

      <div className="flex justify-end pb-2">
        <button
          onClick={() => setSortNewestFirst(!sortNewestFirst)}
          className="flex items-center gap-1.5 px-3 py-1.5 text-[11px] font-semibold text-gray-400 dark:text-gray-500 hover:text-gray-600 dark:hover:text-gray-300 hover:bg-gray-100 dark:hover:bg-gray-800 rounded-full transition-colors"
        >
          <ArrowUp className={`w-3 h-3 transition-transform duration-200 ${sortNewestFirst ? 'rotate-180' : ''}`} />
          <span>{sortNewestFirst ? 'Plus recents en haut' : 'Plus anciens en haut'}</span>
        </button>
      </div>

      {visibleMessages.length === 0 ? (
        <div className="flex flex-col items-center justify-center py-20 text-center animate-fade-in">
          <div className="relative mb-6">
            <div className="w-20 h-20 bg-gradient-to-br from-sky-100 to-teal-100 dark:from-sky-900/30 dark:to-teal-900/30 rounded-3xl flex items-center justify-center rotate-6 transition-transform hover:rotate-0 hover:scale-110 duration-300">
              <MessageSquare className="w-9 h-9 text-sky-500 dark:text-sky-400" />
            </div>
            <div className="absolute -top-2 -right-2 w-8 h-8 bg-gradient-to-br from-amber-100 to-orange-100 dark:from-amber-900/30 dark:to-orange-900/30 rounded-xl flex items-center justify-center -rotate-12 animate-bounce-subtle">
              <span className="text-lg">👋</span>
            </div>
          </div>
          <h3 className="text-lg font-bold text-gray-800 dark:text-gray-200 mb-2">
            Lancez la discussion !
          </h3>
          <p className="text-sm text-gray-500 dark:text-gray-400 max-w-xs leading-relaxed">
            Soyez le premier a participer. Posez une question, partagez un bon plan ou dites simplement bonjour !
          </p>
          <div className="flex gap-2 mt-5">
            {['💬', '🌍', '✈️'].map((emoji, i) => (
              <span key={i} className="text-2xl animate-bounce-subtle" style={{ animationDelay: `${i * 200}ms` }}>
                {emoji}
              </span>
            ))}
          </div>
        </div>
      ) : (
        visibleMessages.map((message, idx) => renderMessageWithReplies(message, 0, idx))
      )}

      {/* Delete modal */}
      {messageToDelete && (
        <div className="fixed inset-0 bg-black/50 backdrop-blur-sm flex items-center justify-center p-4 z-[100]">
          <div className="bg-white dark:bg-gray-900 rounded-2xl shadow-2xl w-full max-w-sm ring-1 ring-black/5 dark:ring-white/5 animate-scale-in">
            <div className="p-5">
              <div className="flex items-center gap-3 mb-3">
                <div className="w-10 h-10 bg-red-50 dark:bg-red-900/20 rounded-xl flex items-center justify-center">
                  <AlertTriangle className="w-5 h-5 text-red-500" />
                </div>
                <h3 className="text-base font-bold text-gray-900 dark:text-white">
                  Confirmer la suppression
                </h3>
              </div>
              <p className="text-sm text-gray-500 dark:text-gray-400 mb-5 ml-[52px]">
                Cette action est irreversible.
              </p>
              <div className="flex justify-end gap-2">
                <button
                  onClick={() => setMessageToDelete(null)}
                  className="px-4 py-2 text-sm font-medium text-gray-600 dark:text-gray-400 hover:bg-gray-100 dark:hover:bg-gray-800 rounded-xl transition-colors"
                >
                  Annuler
                </button>
                <button
                  onClick={handleDelete}
                  className="px-4 py-2 text-sm font-semibold text-white bg-red-600 hover:bg-red-700 rounded-xl transition-colors"
                >
                  Supprimer
                </button>
              </div>
            </div>
          </div>
        </div>
      )}

      {/* Move modal */}
      {showMoveModal && (
        <div className="fixed inset-0 bg-black/50 backdrop-blur-sm flex items-center justify-center p-4 z-[100]">
          <div className="bg-white dark:bg-gray-900 rounded-2xl shadow-2xl w-full max-w-md ring-1 ring-black/5 dark:ring-white/5 animate-scale-in">
            <div className="flex items-center justify-between p-5 border-b border-gray-100 dark:border-gray-800">
              <div className="flex items-center gap-3">
                <div className="w-9 h-9 bg-blue-50 dark:bg-blue-900/20 rounded-xl flex items-center justify-center">
                  <MoveRight className="w-4 h-4 text-blue-600 dark:text-blue-400" />
                </div>
                <h3 className="text-base font-bold text-gray-900 dark:text-white">Deplacer message</h3>
              </div>
              <button
                onClick={() => setShowMoveModal(false)}
                className="p-1.5 text-gray-400 hover:text-gray-600 dark:hover:text-gray-300 hover:bg-gray-100 dark:hover:bg-gray-800 rounded-xl transition-colors"
              >
                <X className="w-4 h-4" />
              </button>
            </div>
            <div className="p-5">
              <div className="mb-4 p-3 bg-gray-50 dark:bg-gray-800/50 rounded-xl">
                <div className="flex items-start gap-2.5">
                  <div className={`w-7 h-7 rounded-full bg-gradient-to-br ${getAvatarColor(messageToMove?.user_id || '')} flex items-center justify-center flex-shrink-0`}>
                    <span className="text-[10px] font-bold text-white">
                      {messageToMove?.username ? messageToMove.username[0].toUpperCase() : '?'}
                    </span>
                  </div>
                  <div className="flex-1 min-w-0">
                    <div className="flex items-center gap-2 mb-0.5">
                      <span className="text-sm font-semibold text-gray-900 dark:text-white truncate">{messageToMove?.username}</span>
                      <span className="text-[11px] text-gray-400 dark:text-gray-500">{messageToMove?.created_at && formatDate(messageToMove.created_at.toDate())}</span>
                    </div>
                    <p className="text-sm text-gray-600 dark:text-gray-400 line-clamp-2">{messageToMove?.content}</p>
                  </div>
                </div>
              </div>
              <div className="mb-5">
                <label className="block text-sm font-semibold text-gray-700 dark:text-gray-300 mb-1.5">Destination</label>
                <select
                  value={targetThread}
                  onChange={(e) => setTargetThread(e.target.value)}
                  className="w-full px-3 py-2.5 text-sm border border-gray-200 dark:border-gray-700 rounded-xl focus:ring-2 focus:ring-blue-500/20 focus:border-blue-400 dark:bg-gray-800 dark:text-white transition-shadow"
                >
                  <option value="">Selectionner la destination</option>
                  <optgroup label="General">
                    {availableThreads.filter(t => t.type !== 'country').map(thread => (
                      <option key={thread.code} value={thread.code} disabled={thread.code === messageToMove?.thread_code}>{thread.name}</option>
                    ))}
                  </optgroup>
                  <optgroup label="Pays">
                    {availableThreads.filter(t => t.type === 'country').map(thread => (
                      <option key={thread.code} value={thread.code} disabled={thread.code === messageToMove?.thread_code}>{thread.name}</option>
                    ))}
                  </optgroup>
                </select>
              </div>
              <div className="flex justify-end gap-2">
                <button onClick={() => setShowMoveModal(false)} className="px-4 py-2 text-sm font-medium text-gray-600 dark:text-gray-400 hover:bg-gray-100 dark:hover:bg-gray-800 rounded-xl transition-colors">Annuler</button>
                <button
                  onClick={executeMove}
                  disabled={!targetThread || isMoving}
                  className="flex items-center gap-1.5 px-4 py-2 text-sm font-semibold text-white bg-blue-600 hover:bg-blue-700 disabled:bg-blue-400 rounded-xl transition-colors"
                >
                  {isMoving ? (
                    <><div className="w-3.5 h-3.5 border-2 border-white/30 border-t-white rounded-full animate-spin" /><span>Deplacement...</span></>
                  ) : (
                    <><MoveRight className="w-3.5 h-3.5" /><span>Deplacer</span></>
                  )}
                </button>
              </div>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}

interface MessageComponentProps {
  message: Message;
  allMessages: Message[];
  isReply?: boolean;
  replyToMessage?: Message | null;
  isAdmin: boolean;
  userInfo: any;
  user: any;
  index: number;
  onDelete: () => void;
  onReply: () => void;
  onReaction: (messageId: string, isLike: boolean) => void;
  onEmojiReaction: (messageId: string, emoji: string) => void;
  onReport: (messageId: string) => void;
  onMove: () => void;
}

function MessageComponent({
  message,
  allMessages,
  isReply = false,
  isAdmin,
  userInfo,
  user,
  index,
  onDelete,
  onReply,
  onReaction,
  onEmojiReaction,
  onReport,
  onMove
}: MessageComponentProps) {
  const [showOptions, setShowOptions] = React.useState(false);
  const [showTooltip, setShowTooltip] = React.useState(false);
  const [showEmojiPicker, setShowEmojiPicker] = React.useState(false);
  const [floatingEmoji, setFloatingEmoji] = React.useState<string | null>(null);
  const [burstingReaction, setBurstingReaction] = React.useState<string | null>(null);
  const optionsRef = React.useRef<HTMLDivElement>(null);
  const tooltipRef = React.useRef<HTMLDivElement>(null);
  const emojiRef = React.useRef<HTMLDivElement>(null);

  const isAdminMessage = React.useMemo(() => {
    return message.is_admin || message.user_id === 'admin' ||
           message.user_id === '1' || message.user_id === '357734' || message.user_id === '301079' || message.user_id === '387526' ||
           (message.user_id && userInfo?.claims?.some((claim: any) =>
             claim.key === 'isAdmin' && claim.value === true &&
             message.user_id === user?.uid
           ));
  }, [message, userInfo, user]);

  const userStats = React.useMemo(() => {
    const userMessages = allMessages.filter(m => m.user_id === message.user_id);
    const totalLikes = userMessages.reduce((sum, m) => sum + (m.likes?.length || 0), 0);
    return { messageCount: userMessages.length, totalLikes };
  }, [allMessages, message.user_id]);

  const helperBadge = React.useMemo(() => {
    if (userStats.totalLikes >= 50) return { label: 'Expert', color: 'bg-amber-100 dark:bg-amber-900/30 text-amber-700 dark:text-amber-400' };
    if (userStats.totalLikes >= 25) return { label: 'Pro', color: 'bg-orange-100 dark:bg-orange-900/30 text-orange-700 dark:text-orange-400' };
    if (userStats.totalLikes >= 10) return { label: 'Actif', color: 'bg-blue-100 dark:bg-blue-900/30 text-blue-700 dark:text-blue-400' };
    if (userStats.totalLikes >= 1) return { label: 'Aidant', color: 'bg-green-100 dark:bg-green-900/30 text-green-700 dark:text-green-400' };
    return null;
  }, [userStats.totalLikes]);

  const avatarColor = React.useMemo(() => {
    if (isAdminMessage) return 'from-amber-400 to-orange-500';
    return getAvatarColor(message.user_id);
  }, [message.user_id, isAdminMessage]);

  const handleEmojiClick = (emoji: string) => {
    onEmojiReaction(message.id, emoji);
    setShowEmojiPicker(false);
    setFloatingEmoji(emoji);
    setBurstingReaction(emoji);
    setTimeout(() => setFloatingEmoji(null), 1000);
    setTimeout(() => setBurstingReaction(null), 400);
  };

  React.useEffect(() => {
    const handleClickOutside = (event: MouseEvent) => {
      if (optionsRef.current && !optionsRef.current.contains(event.target as Node)) setShowOptions(false);
      if (tooltipRef.current && !tooltipRef.current.contains(event.target as Node)) setShowTooltip(false);
      if (emojiRef.current && !emojiRef.current.contains(event.target as Node)) setShowEmojiPicker(false);
    };
    document.addEventListener('mousedown', handleClickOutside);
    return () => document.removeEventListener('mousedown', handleClickOutside);
  }, []);

  const isOwnMessage = message.user_id === user?.uid;
  const emojiReactions = message.emoji_reactions || {};
  const activeEmojis = Object.entries(emojiReactions).filter(([, users]) => users.length > 0);

  return (
    <div
      data-message-id={message.id}
      className={`group animate-message-in ${isReply ? 'mt-2' : 'mt-4 first:mt-0'} ${showOptions ? 'relative z-[60]' : ''}`}
      style={{ animationDelay: `${Math.min(index * 25, 250)}ms` }}
    >
      {/* Reply indicator */}
      {isReply && message.reply_to && (
        <div className="flex items-center gap-1 mb-1 ml-10">
          <CornerDownRight className="w-3 h-3 text-gray-300 dark:text-gray-600" />
          <span className="text-[10px] text-gray-400 dark:text-gray-500">
            en reponse a <span className="font-semibold">{message.reply_to.username}</span>
          </span>
        </div>
      )}

      <div className="flex gap-2.5">
        {/* Avatar */}
        <div className="relative flex-shrink-0" ref={tooltipRef}>
          <button
            onClick={() => setShowTooltip(!showTooltip)}
            className={`w-9 h-9 rounded-xl bg-gradient-to-br ${avatarColor} flex items-center justify-center transition-all duration-200 hover:scale-105 hover:shadow-md ${
              isAdminMessage ? 'ring-2 ring-amber-300/50 dark:ring-amber-500/30' : ''
            }`}
          >
            <span className="text-xs font-bold text-white drop-shadow-sm">
              {message.username ? message.username[0].toUpperCase() : '?'}
            </span>
          </button>

          {showTooltip && (
            <div className="absolute left-full ml-2 top-0 z-50 animate-scale-in">
              <div className="bg-white dark:bg-gray-800 text-gray-900 dark:text-white text-xs rounded-xl px-4 py-3 shadow-xl ring-1 ring-black/5 dark:ring-white/10 whitespace-nowrap">
                <div className="flex items-center gap-2 mb-2">
                  <div className={`w-6 h-6 rounded-lg bg-gradient-to-br ${avatarColor} flex items-center justify-center`}>
                    <span className="text-[9px] font-bold text-white">{message.username?.[0]?.toUpperCase()}</span>
                  </div>
                  <span className="font-bold">{message.username}</span>
                  {isAdminMessage && (
                    <span className="px-1.5 py-0.5 bg-amber-100 dark:bg-amber-900/30 text-amber-700 dark:text-amber-400 text-[9px] font-bold rounded-md">Admin</span>
                  )}
                  {helperBadge && !isAdminMessage && (
                    <span className={`px-1.5 py-0.5 text-[9px] font-bold rounded-md ${helperBadge.color}`}>{helperBadge.label}</span>
                  )}
                </div>
                <div className="space-y-1 text-gray-500 dark:text-gray-400 text-[11px]">
                  <div className="flex items-center gap-1.5">
                    <MessageSquare className="w-3 h-3" />
                    <span>{userStats.messageCount} message{userStats.messageCount > 1 ? 's' : ''}</span>
                  </div>
                  <div className="flex items-center gap-1.5">
                    <ThumbsUp className="w-3 h-3" />
                    <span>{userStats.totalLikes} j'aime recu{userStats.totalLikes > 1 ? 's' : ''}</span>
                  </div>
                </div>
                <div className="flex items-center gap-2 mt-2.5 pt-2 border-t border-gray-100 dark:border-gray-700">
                  <a
                    href={`https://pvtistes.net/membre/${message.username}/`}
                    target="_blank"
                    rel="noopener noreferrer"
                    className="inline-flex items-center gap-1 text-blue-600 dark:text-blue-400 hover:text-blue-700 text-[11px] font-semibold transition-colors"
                  >
                    <User className="w-3 h-3" />
                    <span>Profil</span>
                  </a>
                  {message.user_id !== user?.uid && (
                    <>
                      <span className="text-gray-300 dark:text-gray-600">|</span>
                      <a
                        href={`https://pvtistes.net/forum/private.php?do=newpm&u=${message.user_id}&title=PVTmates`}
                        target="_blank"
                        rel="noopener noreferrer"
                        className="inline-flex items-center gap-1 text-blue-600 dark:text-blue-400 hover:text-blue-700 text-[11px] font-semibold transition-colors"
                      >
                        <Mail className="w-3 h-3" />
                        <span>Message prive</span>
                      </a>
                    </>
                  )}
                </div>
                <div className="absolute right-full top-3 border-[5px] border-transparent border-r-white dark:border-r-gray-800" />
              </div>
            </div>
          )}
        </div>

        {/* Content */}
        <div className="flex-1 min-w-0">
          <div className="flex items-center gap-2 mb-1">
            <span className="text-[13px] font-bold text-gray-900 dark:text-white flex items-center gap-1.5">
              {message.username}
              {isAdminMessage && (
                <span className="inline-flex items-center gap-0.5 px-1.5 py-0.5 bg-amber-100 dark:bg-amber-900/30 text-amber-700 dark:text-amber-400 text-[9px] font-bold rounded-md">
                  <Star className="w-2.5 h-2.5" />
                  Admin
                </span>
              )}
              {helperBadge && !isAdminMessage && (
                <span className={`inline-flex px-1.5 py-0.5 text-[9px] font-bold rounded-md ${helperBadge.color}`}>
                  {helperBadge.label}
                </span>
              )}
            </span>
            <span className="text-[10px] text-gray-400 dark:text-gray-500 font-medium">
              {formatDate(message.created_at.toDate())}
            </span>
          </div>

          {/* Message bubble */}
          <div className="relative">
            <div className={`inline-block max-w-full rounded-2xl px-4 py-2.5 text-[13.5px] leading-relaxed ${
              isAdminMessage
                ? 'bg-gradient-to-r from-amber-50 to-orange-50 dark:from-amber-900/15 dark:to-orange-900/10 text-gray-800 dark:text-gray-200 ring-1 ring-amber-200/50 dark:ring-amber-800/30'
                : isOwnMessage
                  ? 'bg-gradient-to-br from-blue-500 to-blue-600 text-white shadow-sm shadow-blue-500/20'
                  : 'bg-white dark:bg-gray-800/80 text-gray-800 dark:text-gray-200 ring-1 ring-gray-100 dark:ring-gray-700/50 shadow-sm'
            }`}>
              <p
                className="message-text whitespace-pre-wrap break-words"
                dangerouslySetInnerHTML={{
                  __html: message.content.replace(
                    /(https?:\/\/[^\s]+)/g,
                    isAdminMessage
                      ? '<a href="$1" target="_blank" rel="noopener noreferrer" class="text-amber-700 dark:text-amber-400 underline hover:no-underline font-medium">$1</a>'
                      : isOwnMessage
                        ? '<a href="$1" target="_blank" rel="noopener noreferrer" class="text-blue-100 underline hover:no-underline font-medium">$1</a>'
                        : '<a href="$1" target="_blank" rel="noopener noreferrer" class="text-blue-600 dark:text-blue-400 underline hover:no-underline font-medium">$1</a>'
                  )
                }}
              />
            </div>

            {floatingEmoji && (
              <span className="absolute -top-2 left-1/2 text-2xl animate-float-emoji">{floatingEmoji}</span>
            )}

            {/* Reactions (thumbs + emoji) */}
            {(activeEmojis.length > 0 || (message.likes?.length > 0) || (message.dislikes?.length > 0)) && (
              <div className="flex flex-wrap gap-1 mt-1.5">
                {message.likes?.length > 0 && (
                  <button
                    onClick={() => !isOwnMessage && onReaction(message.id, true)}
                    className={`inline-flex items-center gap-1 px-2 py-0.5 rounded-full text-xs transition-all duration-200 ${
                      message.likes.includes(user?.uid || '')
                        ? 'bg-emerald-50 dark:bg-emerald-900/20 ring-1 ring-emerald-200 dark:ring-emerald-800'
                        : 'bg-gray-50 dark:bg-gray-800 ring-1 ring-gray-200 dark:ring-gray-700 hover:ring-gray-300 dark:hover:ring-gray-600'
                    }`}
                  >
                    <ThumbsUp className="w-3 h-3 text-emerald-500 dark:text-emerald-400" />
                    <span className="font-bold text-gray-600 dark:text-gray-400 tabular-nums text-[11px]">{message.likes.length}</span>
                  </button>
                )}
                {message.dislikes?.length > 0 && (
                  <button
                    onClick={() => !isOwnMessage && onReaction(message.id, false)}
                    className={`inline-flex items-center gap-1 px-2 py-0.5 rounded-full text-xs transition-all duration-200 ${
                      message.dislikes.includes(user?.uid || '')
                        ? 'bg-red-50 dark:bg-red-900/20 ring-1 ring-red-200 dark:ring-red-800'
                        : 'bg-gray-50 dark:bg-gray-800 ring-1 ring-gray-200 dark:ring-gray-700 hover:ring-gray-300 dark:hover:ring-gray-600'
                    }`}
                  >
                    <ThumbsDown className="w-3 h-3 text-red-400 dark:text-red-400" />
                    <span className="font-bold text-gray-600 dark:text-gray-400 tabular-nums text-[11px]">{message.dislikes.length}</span>
                  </button>
                )}
                {activeEmojis.map(([emoji, users]) => (
                  <button
                    key={emoji}
                    onClick={() => message.user_id !== user?.uid && handleEmojiClick(emoji)}
                    className={`inline-flex items-center gap-1 px-2 py-0.5 rounded-full text-xs transition-all duration-200 ${
                      users.includes(user?.uid || '')
                        ? 'bg-blue-50 dark:bg-blue-900/20 ring-1 ring-blue-200 dark:ring-blue-800'
                        : 'bg-gray-50 dark:bg-gray-800 ring-1 ring-gray-200 dark:ring-gray-700 hover:ring-gray-300 dark:hover:ring-gray-600'
                    } ${burstingReaction === emoji ? 'animate-reaction-burst' : ''}`}
                  >
                    <span className="text-sm">{emoji}</span>
                    <span className="font-bold text-gray-600 dark:text-gray-400 tabular-nums text-[11px]">{users.length}</span>
                  </button>
                ))}
              </div>
            )}
          </div>

          {/* Action bar */}
          <div className="flex items-center gap-0.5 mt-1 opacity-0 group-hover:opacity-100 transition-opacity duration-150">
            {!isOwnMessage && (
              <>
                <ActionButton
                  active={message.likes?.includes(user?.uid || '')}
                  activeClass="text-emerald-600 dark:text-emerald-400 bg-emerald-50 dark:bg-emerald-900/20"
                  onClick={() => onReaction(message.id, true)}
                >
                  <ThumbsUp className="w-3 h-3" />
                </ActionButton>

                <ActionButton
                  active={message.dislikes?.includes(user?.uid || '')}
                  activeClass="text-red-500 dark:text-red-400 bg-red-50 dark:bg-red-900/20"
                  onClick={() => onReaction(message.id, false)}
                >
                  <ThumbsDown className="w-3 h-3" />
                </ActionButton>

                <div ref={emojiRef} className="relative">
                  <ActionButton onClick={() => setShowEmojiPicker(!showEmojiPicker)}>
                    <Smile className="w-3 h-3" />
                  </ActionButton>
                  {showEmojiPicker && (
                    <div className="absolute bottom-full left-0 mb-1 flex gap-0.5 bg-white dark:bg-gray-800 rounded-xl shadow-xl ring-1 ring-black/5 dark:ring-white/10 px-1.5 py-1.5 z-50 animate-scale-in">
                      {QUICK_EMOJIS.map((emoji) => (
                        <button
                          key={emoji}
                          onClick={() => handleEmojiClick(emoji)}
                          className="w-8 h-8 flex items-center justify-center rounded-lg hover:bg-gray-100 dark:hover:bg-gray-700 transition-all duration-150 hover:scale-125 text-base"
                        >
                          {emoji}
                        </button>
                      ))}
                    </div>
                  )}
                </div>
              </>
            )}

            <ActionButton onClick={onReply}>
              <Reply className="w-3 h-3" />
              <span>Repondre</span>
            </ActionButton>

            <div ref={optionsRef} className="relative ml-auto">
              <button
                onClick={() => setShowOptions(!showOptions)}
                className="p-1.5 text-gray-400 dark:text-gray-500 hover:text-gray-600 dark:hover:text-gray-300 hover:bg-gray-100 dark:hover:bg-gray-800 rounded-lg transition-colors"
              >
                <svg className="w-3.5 h-3.5" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.5">
                  <circle cx="12" cy="12" r="1" /><circle cx="19" cy="12" r="1" /><circle cx="5" cy="12" r="1" />
                </svg>
              </button>
              {showOptions && (
                <div className="absolute right-0 mt-1 w-44 bg-white dark:bg-gray-800 rounded-xl shadow-xl ring-1 ring-black/5 dark:ring-white/10 overflow-hidden z-[100] animate-scale-in">
                  <div className="py-1">
                    {isAdmin && (
                      <>
                        <MenuButton icon={Trash2} label="Supprimer" variant="red" onClick={() => { onDelete(); setShowOptions(false); }} />
                        <MenuButton icon={MoveRight} label="Deplacer" variant="blue" onClick={() => { onMove(); setShowOptions(false); }} />
                        {message.user_id !== user?.uid && <div className="mx-2 my-1 border-t border-gray-100 dark:border-gray-700" />}
                      </>
                    )}
                    {message.user_id !== user?.uid && (
                      <MenuButton icon={Flag} label="Signaler" variant="orange" onClick={() => { onReport(message.id); setShowOptions(false); }} />
                    )}
                  </div>
                </div>
              )}
            </div>
          </div>
        </div>
      </div>
    </div>
  );
}

function ActionButton({
  children,
  onClick,
  active = false,
  activeClass = '',
}: {
  children: React.ReactNode;
  onClick: () => void;
  active?: boolean;
  activeClass?: string;
}) {
  return (
    <button
      onClick={onClick}
      className={`flex items-center gap-1 px-2 py-1 text-[11px] font-medium rounded-lg transition-all duration-200 ${
        active
          ? activeClass
          : 'text-gray-400 dark:text-gray-500 hover:bg-gray-100 dark:hover:bg-gray-800 hover:text-gray-600 dark:hover:text-gray-300'
      }`}
    >
      {children}
    </button>
  );
}

function MenuButton({
  icon: Icon,
  label,
  variant,
  onClick,
}: {
  icon: React.ElementType;
  label: string;
  variant: 'red' | 'blue' | 'orange';
  onClick: () => void;
}) {
  const colors = {
    red: 'text-red-600 dark:text-red-400 hover:bg-red-50 dark:hover:bg-red-900/10',
    blue: 'text-blue-600 dark:text-blue-400 hover:bg-blue-50 dark:hover:bg-blue-900/10',
    orange: 'text-orange-600 dark:text-orange-400 hover:bg-orange-50 dark:hover:bg-orange-900/10',
  };

  return (
    <button
      onClick={onClick}
      className={`w-full flex items-center gap-2.5 px-3 py-2 text-left text-sm font-medium transition-colors ${colors[variant]}`}
    >
      <Icon className="w-3.5 h-3.5" />
      <span>{label}</span>
    </button>
  );
}
